/**
 * Micro-Course Catalog
 *
 * 2–3 courses per broad path (24 courses total).
 * Each course has 3 modules; each module has a lesson + 4 timed quiz questions.
 *
 * Quiz question options carry rawTraitDeltas — the mutation engine applies modifiers
 * (time taken, correctness, difficulty, diminishing returns) before updating the profile.
 *
 * timeLimit is seconds per question.
 */

export interface QuizOption {
  id: string;
  text: string;
  correct: boolean;
  /** Raw deltas applied if this option is chosen */
  rawTraitDeltas: Record<string, number>;
}

export interface QuizQuestion {
  id: string;
  text: string;
  timeLimit: number; // seconds
  difficulty: 1 | 2 | 3;
  explanation: string;
  options: QuizOption[];
}

export interface CourseModule {
  id: string;
  title: string;
  lesson: {
    summary: string;       // 1 sentence hook
    keyPoints: string[];   // 4–5 bullet points students read before the quiz
  };
  questions: QuizQuestion[];
}

export interface MicroCourse {
  id: string;
  broadPathId: string;
  title: string;
  description: string;
  estimatedMinutes: number;
  difficulty: 1 | 2 | 3;
  /** Bonus deltas applied on full course completion (on top of quiz events) */
  completionBonusDeltas: Record<string, number>;
  modules: CourseModule[];
}

// ─────────────────────────────────────────────────────────────────────────────
// STEM & Technology
// ─────────────────────────────────────────────────────────────────────────────
const stemCourses: MicroCourse[] = [
  {
    id: "mc_stem_01",
    broadPathId: "broad_stem_tech",
    title: "Thinking Like a Programmer",
    description: "Learn how programmers break problems into steps, spot patterns, and build logic before writing a single line of code.",
    estimatedMinutes: 12,
    difficulty: 1,
    completionBonusDeltas: { T01: 2, T02: 2, T13: 3, T06: 1 },
    modules: [
      {
        id: "mc_stem_01_m1",
        title: "Decomposition: Breaking Problems Down",
        lesson: {
          summary: "Every complex problem is just a stack of smaller problems — programmers are expert decomposers.",
          keyPoints: [
            "Decomposition means splitting a big problem into sub-problems you can solve one at a time.",
            "Example: 'Build a calculator' → input, parse, compute, display — four smaller problems.",
            "Each sub-problem can be solved, tested, and fixed independently.",
            "Decomposition is also how your brain naturally handles complexity — it just happens unconsciously.",
            "Before coding anything, experienced engineers write the decomposition in plain English first.",
          ],
        },
        questions: [
          {
            id: "mc_stem_01_m1_q1",
            text: "A student wants to build an app that tracks their daily water intake. Which is the BEST first step using decomposition?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "Decomposition means identifying all the sub-problems first — what data to store, how to display it, how to update it — before thinking about code.",
            options: [
              { id: "A", text: "List all the smaller problems the app needs to solve (input, storage, display, reminders)", correct: true, rawTraitDeltas: { T01: 3, T02: 2, T13: 2 } },
              { id: "B", text: "Start coding the app immediately while ideas are fresh", correct: false, rawTraitDeltas: { T40: 1, T13: 0 } },
              { id: "C", text: "Search for a ready-made app instead of building one", correct: false, rawTraitDeltas: { T11: 1 } },
              { id: "D", text: "Design the colour scheme and logo first", correct: false, rawTraitDeltas: { T21: 1 } },
            ],
          },
          {
            id: "mc_stem_01_m1_q2",
            text: "Which of these best describes 'algorithmic thinking'?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "An algorithm is a precise, ordered sequence of steps that solves a problem every time — the foundation of programming.",
            options: [
              { id: "A", text: "Guessing the best approach and trying it", correct: false, rawTraitDeltas: { T40: 1 } },
              { id: "B", text: "Writing a precise step-by-step sequence that solves a problem reliably", correct: true, rawTraitDeltas: { T02: 3, T01: 2, T09: 2 } },
              { id: "C", text: "Using maths to calculate an answer", correct: false, rawTraitDeltas: { T07: 1 } },
              { id: "D", text: "Finding the fastest solution without caring about accuracy", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_01_m1_q3",
            text: "A pattern recognition exercise: 2, 4, 8, 16, __ What comes next?",
            timeLimit: 25,
            difficulty: 1,
            explanation: "Each number doubles — this is a geometric sequence. Pattern recognition underpins algorithms, data analysis, and machine learning.",
            options: [
              { id: "A", text: "18", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "32", correct: true, rawTraitDeltas: { T09: 3, T07: 2, T08: 2 } },
              { id: "C", text: "24", correct: false, rawTraitDeltas: { T07: 1 } },
              { id: "D", text: "20", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_01_m1_q4",
            text: "Which scenario demonstrates 'abstraction' in programming?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Abstraction hides complexity behind a simple interface — you use a function without knowing its inner details, just like pressing a car's accelerator without understanding the engine.",
            options: [
              { id: "A", text: "Memorising all lines of code in a program", correct: false, rawTraitDeltas: { T31: 1 } },
              { id: "B", text: "Using a 'sort()' function without knowing how it sorts internally", correct: true, rawTraitDeltas: { T08: 3, T01: 2, T06: 2 } },
              { id: "C", text: "Writing every function from scratch", correct: false, rawTraitDeltas: { T04: 1, T13: 1 } },
              { id: "D", text: "Commenting every line of code you write", correct: false, rawTraitDeltas: { T47: 2 } },
            ],
          },
        ],
      },
      {
        id: "mc_stem_01_m2",
        title: "Logic Gates and Boolean Thinking",
        lesson: {
          summary: "Computers make every decision using just three operations: AND, OR, NOT — understanding these unlocks how all digital technology works.",
          keyPoints: [
            "Boolean logic deals with only two states: TRUE (1) and FALSE (0).",
            "AND gate: both inputs must be true for output to be true.",
            "OR gate: at least one input must be true for output to be true.",
            "NOT gate: flips the input — true becomes false, false becomes true.",
            "Real example: a door unlocks only if the card AND the PIN are correct (AND gate).",
          ],
        },
        questions: [
          {
            id: "mc_stem_01_m2_q1",
            text: "A security system unlocks if the fingerprint scan OR the keycard is valid. What type of logic is this?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "OR logic means either condition being true is sufficient to produce a true (unlock) output.",
            options: [
              { id: "A", text: "AND logic", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "OR logic", correct: true, rawTraitDeltas: { T02: 3, T04: 2, T06: 1 } },
              { id: "C", text: "NOT logic", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "XOR logic", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_01_m2_q2",
            text: "TRUE AND FALSE = ?",
            timeLimit: 20,
            difficulty: 1,
            explanation: "AND requires BOTH inputs to be TRUE. Since one input is FALSE, the result is FALSE.",
            options: [
              { id: "A", text: "TRUE", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "FALSE", correct: true, rawTraitDeltas: { T02: 2, T07: 2 } },
              { id: "C", text: "MAYBE", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "UNDEFINED", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_01_m2_q3",
            text: "What does NOT(NOT(TRUE)) evaluate to?",
            timeLimit: 30,
            difficulty: 2,
            explanation: "NOT(TRUE) = FALSE. NOT(FALSE) = TRUE. Double negation returns to the original value.",
            options: [
              { id: "A", text: "FALSE", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "TRUE", correct: true, rawTraitDeltas: { T02: 3, T08: 3, T01: 2 } },
              { id: "C", text: "UNDEFINED", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "0", correct: false, rawTraitDeltas: { T07: 1 } },
            ],
          },
          {
            id: "mc_stem_01_m2_q4",
            text: "A smart light turns on ONLY when motion is detected AND it is after sunset. Which gate combination models this?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "Both conditions must be true simultaneously — this is an AND gate.",
            options: [
              { id: "A", text: "OR gate", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "AND gate", correct: true, rawTraitDeltas: { T02: 3, T06: 2, T04: 2 } },
              { id: "C", text: "NOT gate", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "A combination of OR and NOT gates", correct: false, rawTraitDeltas: { T01: 1 } },
            ],
          },
        ],
      },
      {
        id: "mc_stem_01_m3",
        title: "Systems Thinking: Seeing the Whole",
        lesson: {
          summary: "The best engineers don't just fix the broken part — they understand how every part affects every other part.",
          keyPoints: [
            "A system is a set of parts that interact to produce behaviour no single part can produce alone.",
            "Feedback loops: changes in one part circle back and influence the whole system.",
            "Example: Reducing sleep → less focus → lower exam score → stress → even less sleep (a negative feedback loop).",
            "Systems thinkers ask 'what happens downstream?' before making a change.",
            "Most real-world engineering failures are systems failures, not component failures.",
          ],
        },
        questions: [
          {
            id: "mc_stem_01_m3_q1",
            text: "A school increases homework but student grades fall. A systems thinker would first ask:",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Systems thinking looks for secondary effects — increased homework might cause more fatigue, reducing the quality of studying, which lowers grades despite more time spent.",
            options: [
              { id: "A", text: "How much homework is too much?", correct: false, rawTraitDeltas: { T01: 1 } },
              { id: "B", text: "What other factors changed when homework increased? (sleep, stress, time for other subjects)", correct: true, rawTraitDeltas: { T06: 4, T01: 3, T32: 2 } },
              { id: "C", text: "Are students lazy?", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Should homework be abolished?", correct: false, rawTraitDeltas: { T19: 1 } },
            ],
          },
          {
            id: "mc_stem_01_m3_q2",
            text: "Which best describes a 'positive feedback loop'?",
            timeLimit: 40,
            difficulty: 2,
            explanation: "A positive feedback loop amplifies change — more of something produces even more of it, like compound interest or viral spread.",
            options: [
              { id: "A", text: "A system that returns to balance after being disturbed", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "A system where change amplifies itself — more produces even more", correct: true, rawTraitDeltas: { T06: 3, T08: 3, T09: 2 } },
              { id: "C", text: "A system that only goes in one direction and never reverses", correct: false, rawTraitDeltas: { T01: 1 } },
              { id: "D", text: "A system with no feedback at all", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_01_m3_q3",
            text: "In software, a 'microservices architecture' splits one large app into many small independent services. What systems principle is this?",
            timeLimit: 60,
            difficulty: 3,
            explanation: "This is modularisation — breaking a system into loosely coupled components so changes in one don't cascade into others, reducing system fragility.",
            options: [
              { id: "A", text: "Abstraction", correct: false, rawTraitDeltas: { T08: 1 } },
              { id: "B", text: "Modularisation and loose coupling", correct: true, rawTraitDeltas: { T06: 4, T04: 3, T01: 2 } },
              { id: "C", text: "Redundancy", correct: false, rawTraitDeltas: { T36: 1 } },
              { id: "D", text: "Recursion", correct: false, rawTraitDeltas: { T02: 1 } },
            ],
          },
          {
            id: "mc_stem_01_m3_q4",
            text: "A car's cruise control slows down when it detects the car going faster than the set speed. This is an example of:",
            timeLimit: 45,
            difficulty: 2,
            explanation: "This is a negative feedback loop — the system detects deviation from a target and counteracts it to restore balance.",
            options: [
              { id: "A", text: "Positive feedback loop", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Negative feedback loop (stabilising)", correct: true, rawTraitDeltas: { T06: 3, T05: 2, T02: 2 } },
              { id: "C", text: "Open loop control", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Emergent behaviour", correct: false, rawTraitDeltas: { T08: 1 } },
            ],
          },
        ],
      },
    ],
  },

  {
    id: "mc_stem_02",
    broadPathId: "broad_stem_tech",
    title: "Data & Patterns: The Language of Machines",
    description: "Understand how computers see the world through data — numbers, patterns, and statistics that power everything from search engines to AI.",
    estimatedMinutes: 15,
    difficulty: 2,
    completionBonusDeltas: { T09: 2, T10: 2, T07: 2, T01: 1 },
    modules: [
      {
        id: "mc_stem_02_m1",
        title: "What is Data? Raw vs Meaningful",
        lesson: {
          summary: "Data is raw — information is what you get when data is organised to answer a question.",
          keyPoints: [
            "Data alone is meaningless — '42' is just a number until you know it's a temperature, a score, or an age.",
            "Structured data: organised in rows/columns (spreadsheets, databases).",
            "Unstructured data: text, images, audio — harder to analyse but contains more insight.",
            "Data quality matters more than quantity — garbage in, garbage out.",
            "The best data scientists spend 80% of their time cleaning data, 20% analysing it.",
          ],
        },
        questions: [
          {
            id: "mc_stem_02_m1_q1",
            text: "A database has: Name, Age, Score, Date. Which is an example of UNSTRUCTURED data?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "A student's free-text written response doesn't fit neatly into columns — it requires natural language processing to extract meaning.",
            options: [
              { id: "A", text: "A student's numerical exam score", correct: false, rawTraitDeltas: { T07: 1 } },
              { id: "B", text: "A student's written essay about their goals", correct: true, rawTraitDeltas: { T01: 3, T10: 2, T11: 2 } },
              { id: "C", text: "A student's date of birth", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "A student's school ID number", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_02_m1_q2",
            text: "Five students scored: 55, 72, 68, 90, 45. What is the mean score?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "Mean = sum / count = (55+72+68+90+45) / 5 = 330 / 5 = 66.",
            options: [
              { id: "A", text: "68", correct: false, rawTraitDeltas: { T07: 1 } },
              { id: "B", text: "66", correct: true, rawTraitDeltas: { T07: 3, T10: 2, T37: 2 } },
              { id: "C", text: "70", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "72", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_02_m1_q3",
            text: "Which statement about correlation is TRUE?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Correlation shows a relationship between two variables but does NOT prove one causes the other — this is one of the most important distinctions in data analysis.",
            options: [
              { id: "A", text: "Correlation always means one thing causes another", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Correlation shows a relationship but NOT necessarily causation", correct: true, rawTraitDeltas: { T10: 4, T01: 3, T11: 2 } },
              { id: "C", text: "Negative correlation means two things are unrelated", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Correlation only applies to large datasets", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_02_m1_q4",
            text: "A school notices students who eat breakfast score 12% higher on average. A data-savvy student would say:",
            timeLimit: 50,
            difficulty: 2,
            explanation: "This is correlation — the finding is interesting and worth investigating, but we need controlled experiments before claiming breakfast CAUSES better grades.",
            options: [
              { id: "A", text: "Eating breakfast causes better grades — make it compulsory", correct: false, rawTraitDeltas: { T35: 1 } },
              { id: "B", text: "The data is interesting but we need a controlled experiment to test causation", correct: true, rawTraitDeltas: { T10: 4, T11: 3, T01: 2 } },
              { id: "C", text: "The difference isn't meaningful — ignore it", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Only results above 20% are worth noting", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_stem_02_m2",
        title: "Algorithms in Everyday Life",
        lesson: {
          summary: "Every time a streaming app suggests a song, a city traffic system re-routes cars, or your spam filter blocks an email — an algorithm is running.",
          keyPoints: [
            "Algorithms are everywhere: search results, social media feeds, loan approvals, medical diagnoses.",
            "A 'good' algorithm is correct, efficient, and fair — these goals sometimes conflict.",
            "Sorting algorithms arrange data — the choice of algorithm can make a 10-minute task run in seconds.",
            "Machine learning algorithms learn patterns FROM data rather than following hard-coded rules.",
            "Bias in training data creates bias in the algorithm — this is a real-world social problem.",
          ],
        },
        questions: [
          {
            id: "mc_stem_02_m2_q1",
            text: "Why might a social media algorithm showing only content you already agree with be harmful?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "This is called an echo chamber or filter bubble — the algorithm optimises for engagement, which means showing agreeable content, which reduces exposure to differing viewpoints.",
            options: [
              { id: "A", text: "It wastes computing resources", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "It creates an echo chamber, reducing exposure to different perspectives", correct: true, rawTraitDeltas: { T19: 3, T32: 3, T06: 2 } },
              { id: "C", text: "It makes the feed less interesting", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "It has no real-world effect", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_02_m2_q2",
            text: "A bank uses an algorithm trained on historical loan data that approved more male applicants. What is the likely problem?",
            timeLimit: 55,
            difficulty: 2,
            explanation: "Historical data reflects past discrimination. An algorithm trained on biased data learns and perpetuates that bias — a core challenge in ethical AI.",
            options: [
              { id: "A", text: "The algorithm is too complex", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The training data reflects historical gender bias, which the algorithm perpetuates", correct: true, rawTraitDeltas: { T10: 3, T19: 3, T01: 2 } },
              { id: "C", text: "Male applicants simply have better credit", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The bank didn't use enough data", correct: false, rawTraitDeltas: { T11: 1 } },
            ],
          },
          {
            id: "mc_stem_02_m2_q3",
            text: "Which sorting approach is most efficient for a list of 1 million items: checking every possible pair, or dividing the list in half repeatedly?",
            timeLimit: 50,
            difficulty: 3,
            explanation: "Dividing repeatedly (like merge sort or quick sort) is exponentially faster. Checking every pair (bubble sort) on 1M items would take ~500 billion comparisons.",
            options: [
              { id: "A", text: "Checking every possible pair — more thorough", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Dividing in half repeatedly — far fewer operations", correct: true, rawTraitDeltas: { T01: 3, T08: 3, T07: 2 } },
              { id: "C", text: "Both take the same time", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The size of the list doesn't affect the choice", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_02_m2_q4",
            text: "A spam filter is 99% accurate. If 1 in every 10,000 emails is spam, approximately how many legitimate emails will it incorrectly block per million emails?",
            timeLimit: 60,
            difficulty: 3,
            explanation: "With 1M emails and only 100 actual spam emails, the filter still incorrectly flags 1% of 999,900 legitimate emails = ~9,999 false positives. High accuracy on imbalanced data can still cause major problems.",
            options: [
              { id: "A", text: "About 100", correct: false, rawTraitDeltas: { T07: 1 } },
              { id: "B", text: "About 10,000", correct: true, rawTraitDeltas: { T10: 4, T07: 4, T01: 3 } },
              { id: "C", text: "About 1,000", correct: false, rawTraitDeltas: { T07: 1 } },
              { id: "D", text: "Almost none — 99% is very accurate", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_stem_02_m3",
        title: "Spatial Thinking & Engineering Visualisation",
        lesson: {
          summary: "Engineers, architects, and surgeons all share one hidden superpower: they can rotate, build, and deconstruct objects in their minds before touching anything real.",
          keyPoints: [
            "Spatial reasoning is the ability to mentally manipulate 2D and 3D objects.",
            "It's one of the strongest predictors of success in engineering, architecture, and surgery.",
            "It is trainable — like a muscle, it improves with deliberate practice.",
            "Engineering drawings use multiple views (top, front, side) to represent 3D objects on 2D paper.",
            "Reading circuit diagrams, architectural blueprints, or medical scans all require spatial reasoning.",
          ],
        },
        questions: [
          {
            id: "mc_stem_02_m3_q1",
            text: "If you unfold a cube, how many squares do you get?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "A cube has 6 faces — when unfolded (called a net), you get a cross-like arrangement of 6 squares.",
            options: [
              { id: "A", text: "4", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "6", correct: true, rawTraitDeltas: { T03: 3, T07: 2 } },
              { id: "C", text: "8", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "12", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_stem_02_m3_q2",
            text: "An architect draws a building plan showing the view from above. What is this view called?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "A plan view (or top/bird's eye view) shows horizontal layout. Elevation views show vertical faces. Both are essential in architectural drawings.",
            options: [
              { id: "A", text: "Elevation", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Plan view (top/bird's eye view)", correct: true, rawTraitDeltas: { T03: 3, T20: 2 } },
              { id: "C", text: "Section view", correct: false, rawTraitDeltas: { T03: 1 } },
              { id: "D", text: "Isometric view", correct: false, rawTraitDeltas: { T03: 1 } },
            ],
          },
          {
            id: "mc_stem_02_m3_q3",
            text: "A rod is bent at exactly 90°. Looking at it from the front, it looks like an L. What does it look like from the top?",
            timeLimit: 40,
            difficulty: 2,
            explanation: "From the top, both segments are visible — a straight horizontal line with a perpendicular segment extending toward you (or away). Rotating mental models between views is core spatial reasoning.",
            options: [
              { id: "A", text: "A circle", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "An L shape", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "A T shape", correct: false, rawTraitDeltas: { T03: 1 } },
              { id: "D", text: "A straight line with a dot (or line segment perpendicular to it)", correct: true, rawTraitDeltas: { T03: 4, T08: 3 } },
            ],
          },
          {
            id: "mc_stem_02_m3_q4",
            text: "Which profession LEAST requires strong spatial reasoning?",
            timeLimit: 35,
            difficulty: 2,
            explanation: "Accountants primarily work with numerical and financial data — their work is largely abstract and not dependent on visualising 3D space. All other options heavily use spatial reasoning.",
            options: [
              { id: "A", text: "Architect", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Surgeon", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "Accountant", correct: true, rawTraitDeltas: { T01: 2, T03: 1, T07: 2 } },
              { id: "D", text: "Mechanical engineer", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Health & Medical Sciences
// ─────────────────────────────────────────────────────────────────────────────
const healthCourses: MicroCourse[] = [
  {
    id: "mc_health_01",
    broadPathId: "broad_health_medical",
    title: "How the Body Communicates: Introduction to Physiology",
    description: "Explore how the body's systems talk to each other — the signals, messengers, and feedback loops that keep you alive.",
    estimatedMinutes: 12,
    difficulty: 1,
    completionBonusDeltas: { T45: 2, T11: 2, T31: 2, T14: 1 },
    modules: [
      {
        id: "mc_health_01_m1",
        title: "The Nervous System: Your Body's Internet",
        lesson: {
          summary: "Every sensation, thought, and movement is an electrical signal firing through billions of neurons at up to 120 metres per second.",
          keyPoints: [
            "The nervous system has two parts: central (brain + spinal cord) and peripheral (everything else).",
            "Neurons communicate at synapses using chemical messengers called neurotransmitters.",
            "The reflex arc bypasses the brain for speed — your knee jerks before your brain even registers the tap.",
            "The autonomic nervous system controls unconscious functions: heart rate, digestion, breathing.",
            "Fight-or-flight is the sympathetic response; rest-and-digest is the parasympathetic response.",
          ],
        },
        questions: [
          {
            id: "mc_health_01_m1_q1",
            text: "Why does a reflex action (like pulling your hand from a flame) happen before you consciously feel pain?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "The reflex arc sends signals to the spinal cord and back to the muscle directly, bypassing the brain — enabling a faster protective response.",
            options: [
              { id: "A", text: "The brain processes pain instantly", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The signal goes to the spinal cord and back, bypassing the brain for speed", correct: true, rawTraitDeltas: { T45: 3, T02: 2, T31: 2 } },
              { id: "C", text: "Pain receptors don't reach the brain at all", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "It's a voluntary action that happens very fast", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m1_q2",
            text: "A patient has tremors and rigid muscles. Their doctor suspects low dopamine levels. Which condition is most likely?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "Parkinson's disease is characterised by the progressive loss of dopamine-producing neurons, causing the classic motor symptoms of tremor and rigidity.",
            options: [
              { id: "A", text: "Alzheimer's disease", correct: false, rawTraitDeltas: { T45: 1 } },
              { id: "B", text: "Parkinson's disease", correct: true, rawTraitDeltas: { T45: 4, T31: 3, T01: 2 } },
              { id: "C", text: "Multiple sclerosis", correct: false, rawTraitDeltas: { T45: 1 } },
              { id: "D", text: "Epilepsy", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m1_q3",
            text: "The sympathetic nervous system activates during stress. Which response does it NOT trigger?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Increased digestion ('rest and digest') is a parasympathetic response. The sympathetic system suppresses digestion to redirect energy to muscles and brain during stress.",
            options: [
              { id: "A", text: "Increased heart rate", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Dilated pupils", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "Increased digestion", correct: true, rawTraitDeltas: { T45: 3, T31: 3 } },
              { id: "D", text: "Release of adrenaline", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m1_q4",
            text: "What is the primary function of myelin sheaths around nerve fibres?",
            timeLimit: 40,
            difficulty: 2,
            explanation: "Myelin acts as insulation, enabling saltatory conduction — signals jump between nodes of Ranvier, dramatically increasing transmission speed.",
            options: [
              { id: "A", text: "To produce neurotransmitters", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "To speed up electrical signal transmission", correct: true, rawTraitDeltas: { T45: 3, T01: 2, T31: 2 } },
              { id: "C", text: "To protect the brain from toxins", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "To connect neurons across the synaptic cleft", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_health_01_m2",
        title: "Clinical Reasoning: From Symptom to Diagnosis",
        lesson: {
          summary: "Doctors don't guess — they follow a disciplined thought process to eliminate possibilities and arrive at the most probable diagnosis.",
          keyPoints: [
            "Clinical reasoning starts with the chief complaint: the patient's main concern in their own words.",
            "History-taking asks: when did it start, what makes it better/worse, where exactly is the pain?",
            "The differential diagnosis is a ranked list of possible explanations for the symptoms.",
            "Red flag symptoms (sudden severe headache, chest pain radiating to arm) require immediate action.",
            "Pattern recognition is a medical skill — experienced doctors have 'illness scripts' from thousands of cases.",
          ],
        },
        questions: [
          {
            id: "mc_health_01_m2_q1",
            text: "A patient reports a sudden, severe 'thunderclap' headache — the worst of their life. What should a doctor do FIRST?",
            timeLimit: 35,
            difficulty: 2,
            explanation: "A thunderclap headache is a red flag for subarachnoid haemorrhage (brain bleed). This requires immediate investigation, not reassurance.",
            options: [
              { id: "A", text: "Prescribe pain medication and advise rest", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Treat it as a tension headache — very common", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "Treat it as a medical emergency and arrange urgent brain imaging", correct: true, rawTraitDeltas: { T45: 4, T35: 3, T44: 2 } },
              { id: "D", text: "Ask the patient to come back tomorrow if it persists", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m2_q2",
            text: "Which approach best describes evidence-based medicine?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Evidence-based medicine integrates the best available research with clinical expertise and patient values — not just tradition or what 'seems to work'.",
            options: [
              { id: "A", text: "Doing what senior doctors have always done", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Using the best available research evidence combined with clinical expertise and patient preferences", correct: true, rawTraitDeltas: { T45: 4, T11: 3, T01: 2 } },
              { id: "C", text: "Following drug company recommendations", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Treating the most common diagnosis for a given symptom", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m2_q3",
            text: "A 45-year-old presents with fatigue, increased thirst, frequent urination, and blurred vision. What is the MOST likely diagnosis to rule out first?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "The classic triad of increased thirst (polydipsia), frequent urination (polyuria), and blurred vision alongside fatigue strongly suggests Type 2 Diabetes Mellitus.",
            options: [
              { id: "A", text: "Anaemia", correct: false, rawTraitDeltas: { T45: 1 } },
              { id: "B", text: "Type 2 Diabetes Mellitus", correct: true, rawTraitDeltas: { T45: 4, T31: 3, T09: 2 } },
              { id: "C", text: "Kidney infection", correct: false, rawTraitDeltas: { T45: 1 } },
              { id: "D", text: "Hyperthyroidism", correct: false, rawTraitDeltas: { T45: 1 } },
            ],
          },
          {
            id: "mc_health_01_m2_q4",
            text: "Why is it important for a doctor to ask 'what do YOU think is causing this?' to a patient?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "Patient beliefs and concerns (known as ICE — Ideas, Concerns, Expectations) directly affect adherence to treatment. A patient who believes they have cancer won't accept 'it's just stress' without addressing that fear.",
            options: [
              { id: "A", text: "Patients are usually correct and save time", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Understanding patient beliefs improves trust, adherence and helps uncover missed concerns", correct: true, rawTraitDeltas: { T14: 4, T15: 3, T45: 2 } },
              { id: "C", text: "It's only asked to make patients feel involved", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "It helps the doctor avoid doing tests", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_health_01_m3",
        title: "Public Health: When Medicine Meets Society",
        lesson: {
          summary: "The biggest improvements in human lifespan have come not from hospitals, but from clean water, sanitation, and vaccines — public health operates at the population level.",
          keyPoints: [
            "Public health focuses on preventing disease across populations, not treating individuals.",
            "Epidemiology studies how and why diseases spread through populations.",
            "Herd immunity occurs when enough of a population is immune that a pathogen can't easily spread.",
            "Social determinants of health: income, education, housing, and access to food affect health more than medical care.",
            "A single clean water intervention (chlorination) has saved more lives than all antibiotics combined.",
          ],
        },
        questions: [
          {
            id: "mc_health_01_m3_q1",
            text: "During a disease outbreak, which data would a public health official collect FIRST?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "An epidemic curve (epi curve) plots cases over time and reveals the pattern of spread — point source, person-to-person, or mixed — guiding the response strategy.",
            options: [
              { id: "A", text: "The names and personal histories of all patients", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Number of cases over time, location, and common exposure (epidemic curve)", correct: true, rawTraitDeltas: { T10: 4, T43: 3, T11: 2 } },
              { id: "C", text: "The cost of treating each patient", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The hospital capacity in the region", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m3_q2",
            text: "Herd immunity threshold for measles is ~95%. A community has 90% vaccination. What is the risk?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Below the herd immunity threshold, measles can still circulate and reach unvaccinated individuals — those too young, immunocompromised, or unable to be vaccinated.",
            options: [
              { id: "A", text: "No risk — 90% is nearly everyone", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Measles can still circulate and reach vulnerable unvaccinated individuals", correct: true, rawTraitDeltas: { T10: 4, T19: 3, T45: 2 } },
              { id: "C", text: "Only adults are at risk", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The 5% unvaccinated are completely safe due to others' immunity", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m3_q3",
            text: "A poor urban community has high rates of diabetes despite access to a local clinic. What is the MOST likely contributing factor a public health expert would investigate?",
            timeLimit: 55,
            difficulty: 2,
            explanation: "Social determinants of health — food deserts (lack of access to affordable healthy food), stress, unsafe environments for exercise — are often more powerful drivers of chronic disease than access to healthcare.",
            options: [
              { id: "A", text: "The clinic needs better doctors", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Social determinants: food access, stress, income, environment", correct: true, rawTraitDeltas: { T19: 4, T32: 3, T45: 2 } },
              { id: "C", text: "The community doesn't take their medication", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Genetics explains the higher rate", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_health_01_m3_q4",
            text: "A new drug cures a disease in 80% of patients in a clinical trial. What additional information is MOST critical before approving it?",
            timeLimit: 55,
            difficulty: 3,
            explanation: "A 20% side effect rate could be acceptable (if minor) or catastrophic (if severe/permanent). The risk-benefit ratio requires knowing both effectiveness AND safety profile before approval.",
            options: [
              { id: "A", text: "The price of the drug", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The country where it was tested", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "The nature and frequency of side effects in the remaining 20%", correct: true, rawTraitDeltas: { T45: 4, T36: 3, T10: 2 } },
              { id: "D", text: "Whether the drug company is reputable", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Business & Finance
// ─────────────────────────────────────────────────────────────────────────────
const businessCourses: MicroCourse[] = [
  {
    id: "mc_biz_01",
    broadPathId: "broad_business_finance",
    title: "The Economics of Everyday Decisions",
    description: "Economics isn't just numbers — it's the logic behind every choice you, businesses, and governments make with limited resources.",
    estimatedMinutes: 12,
    difficulty: 1,
    completionBonusDeltas: { T37: 2, T35: 2, T34: 1, T01: 1 },
    modules: [
      {
        id: "mc_biz_01_m1",
        title: "Scarcity and Choice: Why Everything Has a Cost",
        lesson: {
          summary: "You can't have everything — scarcity forces choice, and every choice has an opportunity cost.",
          keyPoints: [
            "Scarcity means resources are limited; human wants are unlimited — this tension drives all economic decisions.",
            "Opportunity cost: what you give up when you choose one option over another.",
            "Example: Spending ₦5,000 on a textbook vs. a concert. If you buy the textbook, the concert experience is the opportunity cost.",
            "Rational actors compare marginal costs (the cost of one more unit) vs. marginal benefits.",
            "Governments face scarcity too — spending on roads means less on hospitals.",
          ],
        },
        questions: [
          {
            id: "mc_biz_01_m1_q1",
            text: "A student spends 3 hours on social media instead of studying. What is the opportunity cost?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "The opportunity cost is the highest-value alternative foregone — in this case, the better exam performance and knowledge that 3 hours of studying would have produced.",
            options: [
              { id: "A", text: "The 3 hours of entertainment gained", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The exam performance and knowledge that studying would have produced", correct: true, rawTraitDeltas: { T37: 3, T35: 2, T01: 2 } },
              { id: "C", text: "The cost of internet data", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "There is no opportunity cost since studying wasn't planned", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m1_q2",
            text: "A phone company drops prices by ₦5,000 and sales jump from 200 to 800 units per month. What does this demonstrate?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "This demonstrates the law of demand — as price decreases, quantity demanded increases (all else being equal). The relationship is inverse.",
            options: [
              { id: "A", text: "The law of supply", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The law of demand — lower prices increase quantity demanded", correct: true, rawTraitDeltas: { T37: 3, T07: 2, T09: 2 } },
              { id: "C", text: "Inflation", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The company made a mistake", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m1_q3",
            text: "Nigeria imports more than it exports in a given year. What is this called and what is the likely impact?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "A trade deficit occurs when imports exceed exports. It can weaken the currency (more foreign currency needed to pay for imports) and increase national debt.",
            options: [
              { id: "A", text: "Trade surplus — strengthens the economy", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Trade deficit — can weaken the naira and increase foreign debt", correct: true, rawTraitDeltas: { T37: 3, T34: 2, T36: 2 } },
              { id: "C", text: "Fiscal deficit — causes inflation", correct: false, rawTraitDeltas: { T37: 1 } },
              { id: "D", text: "Capital account surplus — a positive sign", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m1_q4",
            text: "A market has only one supplier of electricity in a city. What market structure is this, and what's the key problem?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "A monopoly is a market with a single seller. The problem: no competition means the monopolist can charge higher prices, provide lower quality, and limit innovation.",
            options: [
              { id: "A", text: "Oligopoly — several dominant firms", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Monopoly — no competition, so high prices and low quality can persist", correct: true, rawTraitDeltas: { T37: 3, T34: 2, T19: 2 } },
              { id: "C", text: "Perfect competition — most efficient market", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Monopsony — single buyer", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_biz_01_m2",
        title: "Entrepreneurship: From Idea to Business",
        lesson: {
          summary: "Every business started with someone who saw a problem no one else was solving — entrepreneurship is applied problem-solving at scale.",
          keyPoints: [
            "An entrepreneur identifies unmet needs and creates a product or service to fill them.",
            "MVP (Minimum Viable Product): the simplest version of your product that lets you test the core idea.",
            "Cashflow is king — profitable businesses fail when they run out of cash.",
            "Market research validates assumptions before you spend money building something nobody wants.",
            "The lean startup method: Build → Measure → Learn → Repeat. Fail fast, fail cheap, learn faster.",
          ],
        },
        questions: [
          {
            id: "mc_biz_01_m2_q1",
            text: "Before spending money building a new app, a smart entrepreneur would first:",
            timeLimit: 40,
            difficulty: 1,
            explanation: "Validating the idea before investing resources is the core of lean entrepreneurship. A simple survey, prototype, or landing page can reveal if real demand exists.",
            options: [
              { id: "A", text: "Register the company and hire developers immediately", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Interview potential customers and validate that the problem exists and people will pay to solve it", correct: true, rawTraitDeltas: { T34: 3, T11: 3, T39: 2 } },
              { id: "C", text: "Build the full app and launch — market validates automatically", correct: false, rawTraitDeltas: { T40: 1 } },
              { id: "D", text: "Wait for a similar idea to succeed elsewhere first", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m2_q2",
            text: "A startup has ₦500,000 revenue and ₦600,000 in expenses. Its bank account shows ₦2,000,000. What is the situation?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "The business is profitable on paper (-₦100,000/month loss? Actually wait — this isn't profitable). The company has cash but is spending more than it earns. Without more investment or growing revenue, it will eventually run out of cash (burn rate problem).",
            options: [
              { id: "A", text: "It's profitable and the ₦2M proves it", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "It's losing ₦100,000/month — the ₦2M is runway, not profit", correct: true, rawTraitDeltas: { T37: 4, T36: 3, T35: 2 } },
              { id: "C", text: "It's fine as long as the bank balance stays positive", correct: false, rawTraitDeltas: { T37: 1 } },
              { id: "D", text: "Revenue doesn't matter — only bank balance does", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m2_q3",
            text: "What does a 'value proposition' mean in business?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "A value proposition is the clear statement of why a customer should choose YOUR product over alternatives — what specific value do you deliver, to whom, and how?",
            options: [
              { id: "A", text: "The price of your product", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "A clear statement of why customers should choose your product and what problem it solves for them", correct: true, rawTraitDeltas: { T39: 3, T17: 2, T34: 2 } },
              { id: "C", text: "The total value of company assets", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Your pitch to investors for funding", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m2_q4",
            text: "Why do most startups fail within 3 years?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Research consistently shows that building something nobody wants (no product-market fit) — not lack of funding, not competition — is the #1 reason startups fail.",
            options: [
              { id: "A", text: "Too much competition from big companies", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "No product-market fit — they built something nobody wanted badly enough to pay for", correct: true, rawTraitDeltas: { T11: 3, T34: 3, T36: 2 } },
              { id: "C", text: "The founders weren't experienced enough", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "They didn't spend enough on marketing", correct: false, rawTraitDeltas: { T17: 1 } },
            ],
          },
        ],
      },
      {
        id: "mc_biz_01_m3",
        title: "Strategic Thinking: How Leaders Plan Ahead",
        lesson: {
          summary: "Strategy is the art of deciding what NOT to do — the best leaders are obsessive about focus, not activity.",
          keyPoints: [
            "Strategy means making deliberate choices about where to compete and how to win.",
            "SWOT analysis: Strengths, Weaknesses, Opportunities, Threats — a snapshot of a company's position.",
            "Competitive advantage: the reason customers choose you over every alternative.",
            "Porter's Five Forces: competition, buyer power, supplier power, substitutes, new entrants.",
            "A good strategy requires saying 'no' to 95% of good opportunities — focus is a strategic weapon.",
          ],
        },
        questions: [
          {
            id: "mc_biz_01_m3_q1",
            text: "Apple charges premium prices for its phones despite cheaper alternatives. What is their competitive advantage?",
            timeLimit: 40,
            difficulty: 1,
            explanation: "Apple's competitive advantage is differentiation — they charge a premium because customers perceive unique value (design, ecosystem, brand, user experience) that justifies the higher price.",
            options: [
              { id: "A", text: "Cost leadership — lowest production cost", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Differentiation — unique value customers pay a premium for", correct: true, rawTraitDeltas: { T34: 3, T39: 2, T01: 2 } },
              { id: "C", text: "Market saturation — everyone has one", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Government protection", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m3_q2",
            text: "A company's SWOT shows: Strong brand, High debt, Growing middle class in new market, New competitor with cheaper product. What is the biggest THREAT?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "The cheaper competitor directly threatens market share. The high debt is a weakness but is internal — the external threat from the new competitor is the most urgent strategic concern.",
            options: [
              { id: "A", text: "High debt", correct: false, rawTraitDeltas: { T37: 1 } },
              { id: "B", text: "The new competitor with a cheaper product", correct: true, rawTraitDeltas: { T34: 3, T36: 3, T35: 2 } },
              { id: "C", text: "The growing middle class", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The strong brand", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_biz_01_m3_q3",
            text: "Why might a successful company deliberately avoid expanding into a profitable adjacent market?",
            timeLimit: 55,
            difficulty: 3,
            explanation: "Strategic focus means staying in areas where you have genuine competitive advantage. Entering a profitable market where you have no edge can dilute resources, confuse customers, and harm your core business — 'strategy is choosing what NOT to do'.",
            options: [
              { id: "A", text: "They don't know about the opportunity", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "They lack funding", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "They have no competitive advantage in that market and entering could dilute their core strength", correct: true, rawTraitDeltas: { T34: 4, T35: 3, T36: 2 } },
              { id: "D", text: "It's too risky to try new things", correct: false, rawTraitDeltas: { T40: 1 } },
            ],
          },
          {
            id: "mc_biz_01_m3_q4",
            text: "In Porter's Five Forces, 'bargaining power of buyers' is HIGH when:",
            timeLimit: 50,
            difficulty: 2,
            explanation: "Buyers have high power when they have many alternatives and can easily switch. They can then demand lower prices or better quality.",
            options: [
              { id: "A", text: "There is only one buyer in the market", correct: false, rawTraitDeltas: { T37: 1 } },
              { id: "B", text: "There are many alternative suppliers and buyers can easily switch", correct: true, rawTraitDeltas: { T34: 3, T01: 2, T37: 2 } },
              { id: "C", text: "The product is unique with no substitutes", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Switching costs are very high", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Creative Arts & Design
// ─────────────────────────────────────────────────────────────────────────────
const creativeCourses: MicroCourse[] = [
  {
    id: "mc_creative_01",
    broadPathId: "broad_creative_arts",
    title: "Design Thinking: Solving Problems Beautifully",
    description: "The same method used by Apple, IDEO, and leading architects — a human-centred approach to creating things people actually love.",
    estimatedMinutes: 12,
    difficulty: 1,
    completionBonusDeltas: { T16: 2, T15: 2, T21: 2, T46: 1 },
    modules: [
      {
        id: "mc_creative_01_m1",
        title: "Empathy: Designing for Real People",
        lesson: {
          summary: "The worst designs come from people who designed for themselves — the best come from people obsessed with understanding others.",
          keyPoints: [
            "Empathy in design means understanding users' needs, frustrations, and goals deeply — not assuming.",
            "Observation: watch people use (or struggle with) a product without interrupting.",
            "Interviews: ask 'why?' five times to get beneath surface answers.",
            "Personas: fictional but research-based profiles of your typical user.",
            "Empathy maps: capture what a user says, thinks, feels, and does.",
          ],
        },
        questions: [
          {
            id: "mc_creative_01_m1_q1",
            text: "A designer wants to improve a hospital waiting area. The BEST first step using design thinking is:",
            timeLimit: 40,
            difficulty: 1,
            explanation: "Empathise first — spend time observing and talking to people who actually use the space (patients, families, nurses) before generating a single design idea.",
            options: [
              { id: "A", text: "Research the latest hospital design trends globally", correct: false, rawTraitDeltas: { T11: 1 } },
              { id: "B", text: "Observe and interview patients, families, and staff in the current waiting area", correct: true, rawTraitDeltas: { T15: 4, T14: 3, T16: 2 } },
              { id: "C", text: "Sketch new floor plan concepts", correct: false, rawTraitDeltas: { T16: 1, T20: 1 } },
              { id: "D", text: "Copy what top-ranked hospitals look like", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m1_q2",
            text: "A student says an app is 'hard to use.' A UX designer asks 'why?' five times. What technique is this?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "The '5 Whys' technique uncovers root causes rather than surface complaints — getting to 'why' the button is confusing reveals an insight that can drive meaningful redesign.",
            options: [
              { id: "A", text: "User testing", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The 5 Whys — drilling to the root cause of a problem", correct: true, rawTraitDeltas: { T15: 3, T43: 3, T01: 2 } },
              { id: "C", text: "A/B testing", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Persona creation", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m1_q3",
            text: "During a design process, a researcher is observing users and NOT correcting them when they make errors with a prototype. Why?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Errors and confusion are gold in user research — they reveal genuine usability problems. Correcting users would mask the real experience and invalidate the research.",
            options: [
              { id: "A", text: "They want to make the users feel bad", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Real errors reveal genuine usability problems — correcting masks the truth", correct: true, rawTraitDeltas: { T15: 4, T47: 3, T11: 2 } },
              { id: "C", text: "The researcher doesn't know the right answer either", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "It saves time to not interrupt", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m1_q4",
            text: "Which of these best describes a 'persona' in UX/design thinking?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "A persona is a fictional character built from real research data — it represents a pattern of behaviour, needs, and goals found across actual users, giving the team a concrete person to design for.",
            options: [
              { id: "A", text: "The designer's favourite type of user", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "A fictional but research-based representation of a target user type", correct: true, rawTraitDeltas: { T15: 3, T14: 2, T16: 2 } },
              { id: "C", text: "The profile of the CEO who commissioned the project", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "An avatar created to make presentations more interesting", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_creative_01_m2",
        title: "Visual Composition: The Rules You Can Break",
        lesson: {
          summary: "Great designers know every rule — and exactly when breaking them creates something unforgettable.",
          keyPoints: [
            "Rule of thirds: place key subjects at the intersection points of a 3×3 grid.",
            "Contrast: opposite elements (dark/light, large/small) create visual tension and hierarchy.",
            "White space (negative space) isn't empty — it's the breath that gives a design clarity.",
            "Typography is 95% of design — the choice of font communicates tone before a single word is read.",
            "Colour psychology: blue = trust, red = urgency/danger, green = growth/health, yellow = optimism.",
          ],
        },
        questions: [
          {
            id: "mc_creative_01_m2_q1",
            text: "A magazine cover places the subject's eyes exactly at the top-right intersection point of a 3×3 grid. What principle is this?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "The rule of thirds places subjects at intersection points to create a more dynamic, visually interesting composition than centring — our eyes naturally travel to these points.",
            options: [
              { id: "A", text: "The golden ratio", correct: false, rawTraitDeltas: { T21: 1 } },
              { id: "B", text: "The rule of thirds", correct: true, rawTraitDeltas: { T20: 3, T21: 3 } },
              { id: "C", text: "Symmetrical balance", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Negative space", correct: false, rawTraitDeltas: { T21: 1 } },
            ],
          },
          {
            id: "mc_creative_01_m2_q2",
            text: "A poster for an emergency clinic uses bright red and bold black text on white. What principle is being used?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "High contrast (red/black on white) creates immediate visual hierarchy and urgency — the eye goes to the most contrasting element first, which is exactly what an emergency communication needs.",
            options: [
              { id: "A", text: "Minimalism", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Contrast — to create urgency and visual hierarchy", correct: true, rawTraitDeltas: { T20: 3, T21: 2, T16: 2 } },
              { id: "C", text: "Analogous colour scheme", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "White space", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m2_q3",
            text: "Apple's product pages have large amounts of empty white space around each product. Why?",
            timeLimit: 40,
            difficulty: 2,
            explanation: "White space is not 'wasted' — it frames and elevates the subject, communicates premium quality, reduces cognitive load, and forces the viewer's attention exactly where the designer wants it.",
            options: [
              { id: "A", text: "Apple couldn't afford more content", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "White space is wasteful but Apple is rich enough not to care", correct: false, rawTraitDeltas: {} },
              { id: "C", text: "White space elevates the product, reduces clutter, and communicates premium quality", correct: true, rawTraitDeltas: { T21: 4, T20: 2, T16: 2 } },
              { id: "D", text: "It's a technical limitation of their website platform", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m2_q4",
            text: "A brand wants to communicate 'reliability and trust.' Which colour palette is most aligned?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "Blue is universally associated with trust, reliability, and stability — this is why banks, healthcare companies, and tech giants (Facebook, LinkedIn, PayPal) predominantly use blue.",
            options: [
              { id: "A", text: "Red and orange", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Blue and navy", correct: true, rawTraitDeltas: { T21: 3, T20: 2, T16: 1 } },
              { id: "C", text: "Neon yellow and pink", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Black and grey only", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      {
        id: "mc_creative_01_m3",
        title: "Prototyping: Making Ideas Real Quickly",
        lesson: {
          summary: "A prototype is a quick, cheap version of an idea built to learn — the goal isn't to build something great, it's to learn something fast.",
          keyPoints: [
            "Fidelity spectrum: paper sketches (low-fi) → wireframes → interactive mockups → working prototype (high-fi).",
            "Low-fi prototypes are tested early — you want to fail fast before investing real resources.",
            "A prototype is a hypothesis made tangible: 'I think this layout will work — let's test it.'",
            "Iteration: each test reveals new insights → update prototype → test again.",
            "The best prototypers are unattached to their ideas — they love being wrong because it means they learned.",
          ],
        },
        questions: [
          {
            id: "mc_creative_01_m3_q1",
            text: "A team spends 3 months building a high-quality prototype before testing with users. What is the main risk?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "The risk is sunk cost bias — after 3 months of work, the team will resist changing or abandoning the prototype even when user tests reveal fundamental problems. Test early, test cheap.",
            options: [
              { id: "A", text: "The prototype will be too good and confuse users", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "They may discover fundamental problems too late, after heavy investment makes changing costly", correct: true, rawTraitDeltas: { T16: 3, T36: 3, T38: 2 } },
              { id: "C", text: "Users prefer finished products — prototypes are confusing", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "No risk — better quality prototypes give better feedback", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m3_q2",
            text: "When should you use a LOW-fidelity (paper/sketch) prototype vs a HIGH-fidelity (interactive) prototype?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "Low-fi is for testing structure, flow, and core concepts early — when fundamental questions are still open. High-fi is for testing details, interactions, and visual design later in the process.",
            options: [
              { id: "A", text: "Always use high-fi — it looks better and impresses stakeholders", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Low-fi to test ideas quickly and cheaply; high-fi to validate details when the concept is validated", correct: true, rawTraitDeltas: { T16: 3, T35: 3, T47: 2 } },
              { id: "C", text: "Low-fi is only for small budgets; high-fi is always better", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Use high-fi first, then simplify to low-fi", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m3_q3",
            text: "A designer says 'I love this design — I hope users love it too.' What mindset problem does this reveal?",
            timeLimit: 40,
            difficulty: 2,
            explanation: "This reveals attachment to the design idea. Good designers design for the user, not for their own taste — they must be willing to completely change or discard a design if users need them to.",
            options: [
              { id: "A", text: "They are very passionate — a positive quality", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Attachment to their own design — risky if users have different needs", correct: true, rawTraitDeltas: { T15: 4, T14: 3, T24: 2 } },
              { id: "C", text: "They haven't done enough research", correct: false, rawTraitDeltas: { T11: 1 } },
              { id: "D", text: "No problem — confidence is important", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_creative_01_m3_q4",
            text: "What does 'iterate based on feedback' mean in the design process?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "Iteration means making incremental improvements based on what you learn from testing — not rebuilding from scratch, but refining the design in cycles until it truly serves the user.",
            options: [
              { id: "A", text: "Discard the prototype and start over", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Make incremental improvements based on what user testing revealed", correct: true, rawTraitDeltas: { T16: 3, T15: 2, T46: 2 } },
              { id: "C", text: "Show the design to more people until someone likes it", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Add more features until the design is complete", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Humanities & Social Sciences
// ─────────────────────────────────────────────────────────────────────────────
const humanitiesCourses: MicroCourse[] = [
  {
    id: "mc_hum_01",
    broadPathId: "broad_humanities_social",
    title: "Understanding People: Introduction to Psychology",
    description: "Why do people do what they do? Psychology gives you tools to understand human behaviour, motivation, and decision-making.",
    estimatedMinutes: 12,
    difficulty: 1,
    completionBonusDeltas: { T14: 2, T23: 2, T19: 1, T32: 1 },
    modules: [
      {
        id: "mc_hum_01_m1",
        title: "Why We Do What We Do: Motivation",
        lesson: {
          summary: "Understanding what drives human behaviour is the foundation of psychology, counselling, management, and design.",
          keyPoints: [
            "Maslow's hierarchy: needs build on each other — safety before belonging, belonging before self-esteem.",
            "Intrinsic motivation comes from within (curiosity, satisfaction); extrinsic from outside (money, grades).",
            "Research shows that external rewards can kill intrinsic motivation — the 'overjustification effect'.",
            "Self-determination theory: autonomy, competence, and relatedness are the three universal psychological needs.",
            "Growth mindset (Dweck): believing ability grows with effort vs fixed mindset (born smart or not).",
          ],
        },
        questions: [
          {
            id: "mc_hum_01_m1_q1",
            text: "A student who loved reading begins receiving prizes for every book they finish. After 6 months, they stop reading voluntarily. What psychology explains this?",
            timeLimit: 50,
            difficulty: 2,
            explanation: "The overjustification effect: external rewards shift the perceived reason for an activity from intrinsic ('I love reading') to extrinsic ('I read for prizes'). Remove the prizes and motivation collapses.",
            options: [
              { id: "A", text: "They got bored of books", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "The overjustification effect — external rewards undermined intrinsic motivation", correct: true, rawTraitDeltas: { T23: 4, T14: 3, T32: 2 } },
              { id: "C", text: "They became lazier over time", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "The prizes weren't valuable enough", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_hum_01_m1_q2",
            text: "According to Maslow, a student cannot focus on studying when they are regularly going hungry. What does this demonstrate?",
            timeLimit: 35,
            difficulty: 1,
            explanation: "Maslow's hierarchy shows physiological needs (food, safety) must be met before higher-level needs like learning and academic achievement can be prioritised.",
            options: [
              { id: "A", text: "The student lacks discipline", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Unmet basic needs prevent higher needs (like learning) from being prioritised", correct: true, rawTraitDeltas: { T14: 3, T19: 3, T32: 2 } },
              { id: "C", text: "Hunger helps people focus", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Schools should only accept well-fed students", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_hum_01_m1_q3",
            text: "Which is an example of INTRINSIC motivation?",
            timeLimit: 30,
            difficulty: 1,
            explanation: "Intrinsic motivation comes from the activity itself — the pleasure of building, creating, or understanding — not from external rewards or pressures.",
            options: [
              { id: "A", text: "Studying to avoid disappointing your parents", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "Coding a game at midnight because you genuinely enjoy the challenge", correct: true, rawTraitDeltas: { T12: 3, T23: 2, T22: 2 } },
              { id: "C", text: "Running to lose weight for an event", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Joining a club to add it to a university application", correct: false, rawTraitDeltas: {} },
            ],
          },
          {
            id: "mc_hum_01_m1_q4",
            text: "A teacher tells a struggling student 'You're just not a maths person.' What does research say about this?",
            timeLimit: 45,
            difficulty: 2,
            explanation: "Research on growth mindset shows that fixed ability labels are harmful and false — mathematical ability is highly trainable. Such statements can create self-limiting beliefs that become self-fulfilling prophecies.",
            options: [
              { id: "A", text: "It's honest feedback that helps the student plan their future", correct: false, rawTraitDeltas: {} },
              { id: "B", text: "This fixed mindset label is harmful — ability in maths is highly trainable with effort", correct: true, rawTraitDeltas: { T30: 4, T14: 3, T19: 2 } },
              { id: "C", text: "It's useful — students should know their natural limits", correct: false, rawTraitDeltas: {} },
              { id: "D", text: "Research is mixed — some people genuinely can't do maths", correct: false, rawTraitDeltas: {} },
            ],
          },
        ],
      },
      { id: "mc_hum_01_m2", title: "Social Influence: How Others Shape Us", lesson: { summary: "Almost every belief, preference, and behaviour you have was shaped by the people and culture around you.", keyPoints: ["Conformity: changing behaviour to match a group — Asch's line experiment showed people deny obvious truth to fit in.", "Obedience to authority: Milgram's study showed ordinary people will follow harmful orders from authority figures.", "Bystander effect: the more people present in an emergency, the less likely any individual will help.", "Social proof: we look to others' behaviour when uncertain — the basis of trends, fads, and peer pressure.", "Cognitive dissonance: discomfort when actions conflict with beliefs — we change beliefs to reduce the discomfort."] }, questions: [ { id: "mc_hum_01_m2_q1", text: "20 people walk past an unconscious person on a busy street without helping. A single person on a quiet road helps immediately. What explains this?", timeLimit: 45, difficulty: 2, explanation: "The bystander effect: in a crowd, each person assumes someone else will help (diffusion of responsibility). Alone, the responsibility is clearly yours.", options: [ { id: "A", text: "City people are less caring", correct: false, rawTraitDeltas: {} }, { id: "B", text: "The bystander effect — diffusion of responsibility in groups", correct: true, rawTraitDeltas: { T19: 4, T23: 3, T32: 2 } }, { id: "C", text: "The 20 people were busy", correct: false, rawTraitDeltas: {} }, { id: "D", text: "People on quiet roads are more alert", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_hum_01_m2_q2", text: "A student buys expensive trainers because all their friends have them, even though they can't afford it. What psychological concept is at work?", timeLimit: 35, difficulty: 1, explanation: "Social proof drives us to use others' behaviour as information about what is correct or desirable — especially when uncertain about what to do or buy.", options: [ { id: "A", text: "Confirmation bias", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Social proof and conformity", correct: true, rawTraitDeltas: { T19: 3, T23: 2, T18: 2 } }, { id: "C", text: "The sunk cost fallacy", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Cognitive dissonance", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_hum_01_m2_q3", text: "After quitting sport, a student starts saying 'sport wasn't that important anyway.' What concept explains this?", timeLimit: 40, difficulty: 2, explanation: "Cognitive dissonance: the student experienced conflict between 'I value sport' and 'I quit sport' — to resolve the discomfort, they changed their belief rather than their action.", options: [ { id: "A", text: "The overjustification effect", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Cognitive dissonance — changing beliefs to align with actions", correct: true, rawTraitDeltas: { T23: 4, T32: 3, T14: 2 } }, { id: "C", text: "Self-actualisation", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Social loafing", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_hum_01_m2_q4", text: "Milgram's obedience study found that ordinary people would administer apparently dangerous electric shocks to strangers when instructed by an authority figure. The key lesson is:", timeLimit: 50, difficulty: 2, explanation: "Milgram showed that situational forces (authority, context) are far more powerful in determining behaviour than character — most of us are capable of causing harm when authority and context normalise it.", options: [ { id: "A", text: "Humans are naturally cruel", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Context and authority can override individual moral judgment — situational forces are powerful", correct: true, rawTraitDeltas: { T32: 4, T19: 3, T42: 2 } }, { id: "C", text: "Psychologists are unethical", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Only obedient people would follow such instructions", correct: false, rawTraitDeltas: {} } ] } ] },
      { id: "mc_hum_01_m3", title: "Communication: The Art of Being Understood", lesson: { summary: "Communication is far more than words — 55% of meaning is conveyed through body language, 38% through tone, only 7% through the actual words.", keyPoints: ["Active listening: full attention, no interrupting, reflecting back what you heard.", "Non-verbal communication: facial expressions, posture, eye contact — often more honest than words.", "The communication gap: what you say ≠ what they hear. Context, tone, and history all distort messages.", "Assertive communication: expressing needs clearly without aggression or passivity.", "Feedback loops: checking understanding ('What I'm hearing is...') prevents misunderstandings from escalating."] }, questions: [ { id: "mc_hum_01_m3_q1", text: "During a difficult conversation, your friend says 'I'm fine' but crosses their arms, avoids eye contact, and speaks quietly. What should you pay most attention to?", timeLimit: 40, difficulty: 1, explanation: "Non-verbal cues (body language, tone) are generally more reliable than words, especially when someone is uncomfortable. The discrepancy between 'I'm fine' and the body language signals they are not fine.", options: [ { id: "A", text: "Their words — they said they're fine", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Their body language and tone — non-verbal signals are more revealing than words", correct: true, rawTraitDeltas: { T14: 4, T23: 3, T18: 2 } }, { id: "C", text: "Nothing — they clearly don't want to talk", correct: false, rawTraitDeltas: {} }, { id: "D", text: "The context of the conversation", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_hum_01_m3_q2", text: "After a group presentation, a student says 'The slides were confusing because no one helped me prepare them.' This is an example of:", timeLimit: 35, difficulty: 1, explanation: "This is defensive communication — shifting blame rather than taking responsibility. It creates conflict and shuts down productive feedback.", options: [ { id: "A", text: "Assertive communication", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Defensive communication — blame-shifting instead of accountability", correct: true, rawTraitDeltas: { T17: 3, T23: 2, T18: 2 } }, { id: "C", text: "Passive communication", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Honest feedback", correct: false, rawTraitDeltas: { T42: 1 } } ] }, { id: "mc_hum_01_m3_q3", text: "A student says 'When you interrupt me mid-sentence, I feel like my ideas don't matter to you.' Which communication style is this?", timeLimit: 40, difficulty: 2, explanation: "This is assertive communication using an 'I-statement' — it expresses the impact of behaviour without attacking the person, reducing defensiveness and opening dialogue.", options: [ { id: "A", text: "Aggressive communication", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Assertive communication using an I-statement", correct: true, rawTraitDeltas: { T17: 4, T28: 3, T23: 2 } }, { id: "C", text: "Passive-aggressive communication", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Passive communication", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_hum_01_m3_q4", text: "In a meeting, the facilitator says 'What I'm hearing is that the main concern is the timeline, not the budget. Is that right?' What technique is this?", timeLimit: 35, difficulty: 1, explanation: "This is reflective listening / paraphrasing — checking understanding by summarising what was said. It prevents assumptions and shows the speaker they were genuinely heard.", options: [ { id: "A", text: "Leading the discussion to a conclusion", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Reflective listening / paraphrasing to confirm understanding", correct: true, rawTraitDeltas: { T17: 3, T14: 3, T18: 2 } }, { id: "C", text: "Interrupting the speaker", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Summarising only to save time", correct: false, rawTraitDeltas: {} } ] } ] },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Law, Education, Agriculture — 1 course each (skeletal, to be expanded)
// ─────────────────────────────────────────────────────────────────────────────
const lawCourse: MicroCourse = {
  id: "mc_law_01", broadPathId: "broad_law_governance",
  title: "Justice, Rights & Governance: How Societies Make Rules",
  description: "Explore how laws are made, why they matter, and what it means to argue for justice with evidence and logic.",
  estimatedMinutes: 12, difficulty: 1,
  completionBonusDeltas: { T42: 2, T17: 2, T43: 1, T02: 1 },
  modules: [
    { id: "mc_law_01_m1", title: "What is Law? Rights, Rules & Justice", lesson: { summary: "Laws are not just rules — they are society's agreed answer to: what is fair, what is safe, and who is protected.", keyPoints: ["Law serves four functions: establish order, protect rights, promote justice, enable cooperation.", "Constitutional law is the supreme law — all other laws must conform to it.", "Human rights are universal claims individuals have regardless of what governments say.", "Rule of law: even the most powerful must answer to the same law as everyone else.", "Legal systems: common law (judge-made precedent) vs civil law (written codes) vs customary law."] }, questions: [ { id: "mc_law_01_m1_q1", text: "A government passes a law requiring people of a certain religion to register with authorities. A constitutional court overturns it. Why?", timeLimit: 45, difficulty: 2, explanation: "Constitutional law overrides ordinary legislation — if a law violates fundamental rights (equality, freedom of religion), constitutional courts can and must strike it down.", options: [ { id: "A", text: "The court had personal objections to the religion", correct: false, rawTraitDeltas: {} }, { id: "B", text: "The law violated constitutional rights — specifically equality and freedom of religion", correct: true, rawTraitDeltas: { T42: 4, T02: 3, T19: 2 } }, { id: "C", text: "Courts can override any law they disagree with", correct: false, rawTraitDeltas: {} }, { id: "D", text: "The government didn't follow proper procedure", correct: false, rawTraitDeltas: { T43: 1 } } ] }, { id: "mc_law_01_m1_q2", text: "Which best describes 'rule of law'?", timeLimit: 35, difficulty: 1, explanation: "Rule of law means the law applies equally to everyone — including rulers, police, and powerful institutions. No one is above the law.", options: [ { id: "A", text: "Rulers make the law and are exempt from it", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Everyone — including rulers — is equally subject to the same law", correct: true, rawTraitDeltas: { T42: 3, T19: 2, T17: 2 } }, { id: "C", text: "Laws that are popular are more valid than unpopular ones", correct: false, rawTraitDeltas: {} }, { id: "D", text: "The military enforces the law", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_law_01_m1_q3", text: "In common law systems, judges rely heavily on 'precedent.' What does this mean?", timeLimit: 40, difficulty: 2, explanation: "Precedent (stare decisis) means judges must follow decisions made in previous similar cases — ensuring consistency, predictability, and equality in how the law is applied.", options: [ { id: "A", text: "Judges follow orders from the government", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Judges follow previous court decisions in similar cases to ensure consistency", correct: true, rawTraitDeltas: { T43: 3, T02: 3, T31: 2 } }, { id: "C", text: "The most popular legal opinion always wins", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Newer laws always override older court decisions", correct: false, rawTraitDeltas: { T02: 1 } } ] }, { id: "mc_law_01_m1_q4", text: "A 16-year-old is accused of a crime. Their lawyer argues they should be tried in a juvenile court. What principle is at work?", timeLimit: 40, difficulty: 1, explanation: "Juvenile justice recognises that minors have different levels of culpability and capacity — the legal system treats them differently to prioritise rehabilitation over punishment.", options: [ { id: "A", text: "Young people should not be held accountable for crimes", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Different legal treatment for minors based on age, maturity, and rehabilitation goals", correct: true, rawTraitDeltas: { T42: 3, T14: 2, T02: 2 } }, { id: "C", text: "Lawyers can always choose which court to use", correct: false, rawTraitDeltas: {} }, { id: "D", text: "All crimes are treated the same regardless of age", correct: false, rawTraitDeltas: {} } ] } ] },
    { id: "mc_law_01_m2", title: "Argumentation: Building a Case", lesson: { summary: "Whether you're a lawyer, policy maker, or debater — the ability to construct a logical, evidence-based argument is the most powerful professional skill.", keyPoints: ["A legal argument has: claim (what you assert) + evidence (facts that support it) + reasoning (why the evidence supports the claim).", "Burden of proof: the person making the claim must prove it — not the other person's job to disprove.", "Counterargument: explicitly address the strongest opposing view and show why your position still holds.", "Ad hominem: attacking the person instead of the argument — always a sign of weak reasoning.", "The strongest argument is one that honestly acknowledges its own weaknesses and still holds."] }, questions: [ { id: "mc_law_01_m2_q1", text: "A lawyer says 'My client couldn't have committed the crime because he's an upstanding member of the community.' What is wrong with this argument?", timeLimit: 40, difficulty: 2, explanation: "This is an irrelevant appeal to character — whether someone is upstanding or not doesn't logically determine whether they committed a specific act. It's a logical fallacy.", options: [ { id: "A", text: "Nothing — character matters in legal proceedings", correct: false, rawTraitDeltas: {} }, { id: "B", text: "It's a logical fallacy — character doesn't prove or disprove the specific act", correct: true, rawTraitDeltas: { T02: 4, T43: 3, T17: 2 } }, { id: "C", text: "The lawyer should mention more community work", correct: false, rawTraitDeltas: {} }, { id: "D", text: "It's too emotional", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_law_01_m2_q2", text: "In a debate, your opponent responds to your argument by saying 'You only believe that because your parents told you to.' This is:", timeLimit: 35, difficulty: 1, explanation: "An ad hominem attack — attacking the person or their background instead of the argument itself. It doesn't engage with whether the argument is actually true or valid.", options: [ { id: "A", text: "A valid counterargument", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Ad hominem — attacking the person, not the argument", correct: true, rawTraitDeltas: { T02: 3, T17: 3, T28: 2 } }, { id: "C", text: "Emotional intelligence", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Asking for clarification", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_law_01_m2_q3", text: "Which argument is strongest?", timeLimit: 50, difficulty: 2, explanation: "The strongest argument is one that presents specific evidence, addresses the most compelling objection, AND still holds — this shows intellectual honesty and strength under scrutiny.", options: [ { id: "A", text: "One that cites three facts and ignores counterarguments", correct: false, rawTraitDeltas: { T17: 1 } }, { id: "B", text: "One with no counterarguments because the position is obviously correct", correct: false, rawTraitDeltas: {} }, { id: "C", text: "One that acknowledges the strongest opposing view and shows why the position still holds", correct: true, rawTraitDeltas: { T02: 4, T17: 3, T43: 2 } }, { id: "D", text: "One that uses the most emotional language", correct: false, rawTraitDeltas: { T17: 1 } } ] }, { id: "mc_law_01_m2_q4", text: "A government claims a new law will reduce crime by 40%. What should a critical thinker ask first?", timeLimit: 45, difficulty: 2, explanation: "Before accepting any statistical claim, a critical thinker asks: what's the evidence, how was it measured, who measured it, and are there alternative explanations? Numbers aren't automatically credible.", options: [ { id: "A", text: "Which party introduced the law", correct: false, rawTraitDeltas: {} }, { id: "B", text: "What evidence supports this claim, how was it measured, and who conducted the study", correct: true, rawTraitDeltas: { T43: 4, T11: 3, T02: 3 } }, { id: "C", text: "Whether crime rates have been rising", correct: false, rawTraitDeltas: { T01: 1 } }, { id: "D", text: "How the law is written", correct: false, rawTraitDeltas: {} } ] } ] },
    { id: "mc_law_01_m3", title: "Governance & Democracy: Who Has the Power?", lesson: { summary: "Democracy is not just voting — it's a system of checks, balances, and accountability designed to prevent any one person or group from having unchecked power.", keyPoints: ["Separation of powers: executive, legislative, judicial — each limits the others.", "Accountability: those in power must answer for their decisions to citizens.", "Civil society: NGOs, media, and citizens play a role outside formal government.", "Corruption: abuse of power for private gain — undermines trust and development.", "Nigeria's constitution (1999 as amended) — presidential system, federal structure, 36 states."] }, questions: [ { id: "mc_law_01_m3_q1", text: "In a presidential system, the president signs a law but the Supreme Court strikes it down as unconstitutional. What does this demonstrate?", timeLimit: 40, difficulty: 1, explanation: "This is the judicial check on executive power — the separation of powers ensures no branch has unchecked authority. Even the president cannot act outside the constitution.", options: [ { id: "A", text: "The Supreme Court has more power than the president", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Separation of powers — the judiciary checks executive and legislative overreach", correct: true, rawTraitDeltas: { T42: 3, T02: 2, T19: 2 } }, { id: "C", text: "Democracy has failed", correct: false, rawTraitDeltas: {} }, { id: "D", text: "The law was unpopular", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_law_01_m3_q2", text: "Free press is considered a 'pillar of democracy.' Why?", timeLimit: 40, difficulty: 1, explanation: "A free press investigates and exposes government misconduct, informs citizens, and enables accountability — without it, corruption is easier to hide and citizens cannot make informed choices.", options: [ { id: "A", text: "Journalists are more educated than politicians", correct: false, rawTraitDeltas: {} }, { id: "B", text: "A free press investigates power, informs citizens, and enables accountability", correct: true, rawTraitDeltas: { T19: 3, T42: 3, T43: 2 } }, { id: "C", text: "Press freedom ensures only positive news is published", correct: false, rawTraitDeltas: {} }, { id: "D", text: "It increases newspaper sales", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_law_01_m3_q3", text: "Why might a country with elections still not be considered a full democracy?", timeLimit: 50, difficulty: 2, explanation: "Elections alone aren't sufficient — a democracy also requires free press, independent judiciary, protection of minority rights, civil liberties, and rule of law. Without these, elections become tools of legitimising authoritarian control.", options: [ { id: "A", text: "Because voting is a limited form of participation", correct: false, rawTraitDeltas: { T19: 1 } }, { id: "B", text: "Because democracy also requires free press, independent courts, civil liberties and rule of law", correct: true, rawTraitDeltas: { T19: 4, T32: 3, T42: 2 } }, { id: "C", text: "It can't — any country with elections is a democracy by definition", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Because small countries can't be real democracies", correct: false, rawTraitDeltas: {} } ] }, { id: "mc_law_01_m3_q4", text: "A public official awards a government contract to their relative's company without a tender process. What is this?", timeLimit: 30, difficulty: 1, explanation: "This is nepotism — a form of corruption where personal relationships rather than merit determine allocation of public resources, undermining fairness and economic efficiency.", options: [ { id: "A", text: "Efficient use of trusted resources", correct: false, rawTraitDeltas: {} }, { id: "B", text: "Nepotism — a form of corruption that undermines fair competition and public trust", correct: true, rawTraitDeltas: { T42: 3, T19: 2, T43: 2 } }, { id: "C", text: "Normal business practice", correct: false, rawTraitDeltas: {} }, { id: "D", text: "Public-private partnership", correct: false, rawTraitDeltas: {} } ] } ] },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Assemble full catalog
// ─────────────────────────────────────────────────────────────────────────────
export const MICRO_COURSE_CATALOG: MicroCourse[] = [
  ...stemCourses,
  ...healthCourses,
  ...businessCourses,
  ...creativeCourses,
  ...humanitiesCourses,
  lawCourse,
];

/** Get all courses for a given broad path */
export function getCoursesForPath(broadPathId: string): MicroCourse[] {
  return MICRO_COURSE_CATALOG.filter((c) => c.broadPathId === broadPathId);
}

/** Get a single course by ID */
export function getCourseById(courseId: string): MicroCourse | undefined {
  return MICRO_COURSE_CATALOG.find((c) => c.id === courseId);
}
